import type React from 'react'
import { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

export interface TextInputProps {
  type?: string
  label?: string
  onChange: Function
  name?: string
  required?: boolean
  value: any
  disabled?: boolean
  maxLength?: number
  minLength?: number
  onError?: Function
  regex?: RegExp
}

interface ITextInputContainer {
  focused: boolean
  disabled?: boolean
  error: boolean
}

const TextInputContainer = styled.div<ITextInputContainer>`
  margin: 10px 5px;
  background: ${props => (props.disabled ? '#f2f2f2' : 'white')};
  border-style: solid;
  border-color: rgba(188, 190, 192, 0.5);
  border-width: 1px;
  padding: 18px;
  border-radius: 5px;
  position: relative;
  text-align: left;
  cursor: text;
  border-color: ${(props: ITextInputContainer) => getColour(props)};
`

function getColour({ focused, error }: ITextInputContainer) {
  if (error) {
    return '#ed1c24'
  }
  if (focused) {
    return '#34bebd'
  }

  return '#55616b'
}

const TextInputElement = styled.input`
  outline: none;
  border: none;
  background: none;
  width: 100%;

  &:disabled {
    background: #f2f2f2;
  }
`

const ErrorContainer = styled.span`
  font-family: Lato, sans-serif;
  text-align: left;
  font-size: 10px;
  letter-spacing: 0.15px;
  color: #ed1c24;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-top: -10px;

  div span {
    margin: 5px;
    padding-top: 1px;
    padding-left: 1px;
    width: 16px;
    height: 16px;
    display: inline-block;
    background-color: #e86b70;
    border-radius: 15px;
    font-size: 10px;
    color: #fff;
    text-align: center;
    margin-right: 10px;
  }
`

const TextInputLabel = styled.span<ITextInputContainer>`
  border-radius: 2px;
  position: absolute;
  color: ${props => (props.focused ? '#34bebd' : '#bcbec0')};
  color: ${(props: ITextInputContainer) => (props.focused ? getColour(props) : '')};
  transform: ${props => (props.focused ? 'translateY(-29px) scale(0.71)' : '')};
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  cursor: text;
  white-space: nowrap;
  font-size: 14px;
  background: ${props => (props.focused ? 'white' : 'transparent')};
  padding: 2px 8px;
`

const TextInput: React.FC<TextInputProps> = ({
  label = '',
  onChange,
  name = '',
  required,
  type = 'text',
  value,
  disabled = false,
  maxLength,
  minLength,
  onError = () => {},
  regex,
}) => {
  const [errors, setErrors] = useState<string[]>([])
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(document.createElement('input'))

  function handleBlur() {
    if (disabled) return
    if (inputRef.current.value.trim() === '') {
      setFocused(false)
    } else {
      setFocused(true)
    }
    setShowErrors(true)
  }

  function handleFocus() {
    if (disabled) return

    inputRef.current.focus()
    setFocused(true)
  }

  useEffect(() => {
    if (inputRef.current.value.trim() !== '') {
      setFocused(true)
    } else {
      setFocused(false)
    }

    const timer = setTimeout(() => {
      if (value != null && String(value) !== '') {
        setShowErrors(true)
      }
    }, 2000) // miliseconds to displaying errors

    runValidation()

    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function runValidation() {
    const foundErrors: string[] = []
    const stringValue = String(value ?? '')

    if (type === 'email') {
      if (
        !stringValue.includes('@') ||
        !stringValue.includes('.') ||
        stringValue.length < 6
      ) {
        foundErrors.push('Email address is not correctly formatted')
      }
    }

    if (regex && !regex.test(stringValue)) {
      foundErrors.push('Ooops that input does not look right.')
    }

    if (minLength && minLength > stringValue.length) {
      foundErrors.push('Not enough characters')
    }

    if (required && stringValue.length === 0) {
      foundErrors.push('This field is required')
    }

    setErrors(foundErrors)

    if (onError) {
      onError(foundErrors)
    }
  }

  return (
    <>
      <TextInputContainer
        error={errors.length > 0 && showErrors}
        disabled={disabled}
        focused={focused}
        onClick={() => handleFocus()}
      >
        <TextInputLabel
          error={errors.length > 0 && showErrors}
          focused={focused}
          id="text-input"
          onClick={() => setFocused(true)}
          onFocus={() => handleFocus()}
          onBeforeInputCapture={() => handleFocus()}
          onBlur={() => handleBlur()}
        >
          {`${label}${required ? '*' : ''}`}
        </TextInputLabel>
        <TextInputElement
          type={type || 'text'}
          name={name}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          value={value ?? ''}
          ref={inputRef}
          onClick={() => setFocused(true)}
          onBlur={() => handleBlur()}
          onChange={e => onChange(e)}
          onFocus={() => handleFocus()}
          onBeforeInputCapture={() => handleFocus()}
          tabIndex={0}
        />
      </TextInputContainer>
      {showErrors && (
        <ErrorContainer>
          {errors.map((err, index) => (
            <div key={index}>
              <span id="text-input-error">x</span>
              {err}
            </div>
          ))}
        </ErrorContainer>
      )}
    </>
  )
}

export default TextInput
